import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch, getApiList } from '../api';
import { getUser, logout } from '../auth';

function Home() {
  const navigate = useNavigate();
  const user = getUser();

  const [dashboard, setDashboard] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [stores, setStores] = useState([]);
  const [wallets, setWallets] = useState([]);
  const [users, setUsers] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [generalNotes, setGeneralNotes] = useState([]);
  const [homeFinanceSummary, setHomeFinanceSummary] = useState(null);
  const [homeAlerts, setHomeAlerts] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [notePriority, setNotePriority] = useState('normal');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteDeletingId, setNoteDeletingId] = useState('');
  const [noteError, setNoteError] = useState('');
  const [noteSuccess, setNoteSuccess] = useState('');
  const [searchText, setSearchText] = useState('');
  const [loading, setLoading] = useState(true);
  const [dataError, setDataError] = useState('');
  const [homeBroadcast, setHomeBroadcast] = useState(null);
  const [broadcastReadLoading, setBroadcastReadLoading] = useState(false);

  useEffect(() => {
    fetchHomeData();
  }, []);

  const fetchJsonSafe = async (path) => {
    try {
      const response = await apiFetch(path);

      if (!response || !response.ok) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error(`Error loading ${path}:`, error);
      return null;
    }
  };

  const fetchHomeData = async () => {
    try {
      setLoading(true);
      setDataError('');

      const [
        dashboardData,
        transactionsData,
        storesData,
        walletsData,
        usersData,
        notificationsData,
        generalNotesData,
        financeSummaryData,
        latestBroadcastData,
        homeAlertsData
      ] = await Promise.all([
        fetchJsonSafe('/dashboard'),
        fetchJsonSafe('/transactions'),
        fetchJsonSafe('/stores'),
        fetchJsonSafe('/wallets'),
        fetchJsonSafe('/users'),
        fetchJsonSafe('/notifications'),
        fetchJsonSafe('/general-notes'),
        fetchJsonSafe('/home/finance-summary'),
        fetchJsonSafe('/notifications/latest-broadcast'),
        fetchJsonSafe('/home/alerts')
      ]);

      setDashboard(dashboardData?.dashboard || dashboardData || null);
      setTransactions(getApiList(transactionsData, 'transactions'));
      setStores(getApiList(storesData, 'stores'));
      setWallets(getApiList(walletsData, 'wallets'));
      setUsers(getApiList(usersData, 'users'));
      setNotifications(
        Array.isArray(notificationsData)
          ? notificationsData
          : notificationsData?.notifications || []
      );
      setGeneralNotes(
        Array.isArray(generalNotesData)
          ? generalNotesData
          : generalNotesData?.notes || []
      );
      setHomeFinanceSummary(financeSummaryData || null);
      setHomeBroadcast(latestBroadcastData?.notification || null);
      setHomeAlerts(homeAlertsData || null);
    } catch (error) {
      console.error(error);
      setDataError('حدث خطأ في تحميل بيانات الواجهة الرئيسية');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openPage = (path) => {
    if (path === '/settings' || path === '/notifications') {
      window.location.href = path;
      return;
    }

    navigate(path);
  };

  const markHomeBroadcastRead = async () => {
    if (!homeBroadcast?.id) return;
    try {
      setBroadcastReadLoading(true);
      await apiFetch(`/notifications/${homeBroadcast.id}/read`, { method: 'POST' });
      setHomeBroadcast(null);
      fetchHomeData();
    } catch (error) {
      console.error(error);
    } finally {
      setBroadcastReadLoading(false);
    }
  };

  const canDeleteNote = (note) => {
    return user?.role === 'owner' || note.user_id === user?.id;
  };

  const handleCreateNote = async (e) => {
    e.preventDefault();

    const text = noteText.trim();

    if (!text) {
      setNoteError('نص الملاحظة مطلوب');
      return;
    }

    if (text.length > 1000) {
      setNoteError('الملاحظة طويلة جداً، الحد الأقصى 1000 حرف');
      return;
    }

    try {
      setNoteSaving(true);
      setNoteError('');
      setNoteSuccess('');

      const response = await apiFetch('/general-notes', {
        method: 'POST',
        body: JSON.stringify({
          note: text,
          priority: notePriority
        })
      });

      if (!response) {
        setNoteError('لم يتم استلام رد من السيرفر');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setNoteError(data.message || data.error || 'فشل إنشاء الملاحظة');
        return;
      }

      setNoteText('');
      setNotePriority('normal');
      setNoteSuccess('تم نشر الملاحظة للجميع');
      await fetchHomeData();
    } catch (err) {
      console.error(err);
      setNoteError('حدث خطأ أثناء إنشاء الملاحظة');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleDeleteNote = async (note) => {
    if (!canDeleteNote(note)) {
      setNoteError('لا يمكنك حذف هذه الملاحظة. الحذف مسموح للمنشئ أو المالك فقط.');
      return;
    }

    const confirmed = window.confirm('هل تريد حذف هذه الملاحظة؟');
    if (!confirmed) return;

    try {
      setNoteDeletingId(note.id);
      setNoteError('');
      setNoteSuccess('');

      const response = await apiFetch(`/general-notes/${note.id}`, {
        method: 'DELETE'
      });

      if (!response) {
        setNoteError('لم يتم استلام رد من السيرفر');
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        setNoteError(data.message || data.error || 'فشل حذف الملاحظة');
        return;
      }

      setNoteSuccess('تم حذف الملاحظة');
      await fetchHomeData();
    } catch (err) {
      console.error(err);
      setNoteError('حدث خطأ أثناء حذف الملاحظة');
    } finally {
      setNoteDeletingId('');
    }
  };

  const normalizeArabic = (value) => {
    return String(value || '')
      .toLowerCase()
      .replace(/[أإآا]/g, 'ا')
      .replace(/[ى]/g, 'ي')
      .replace(/[ؤ]/g, 'و')
      .replace(/[ئ]/g, 'ي')
      .replace(/[ة]/g, 'ه')
      .replace(/[٠-٩]/g, (digit) => '٠١٢٣٤٥٦٧٨٩'.indexOf(digit))
      .replace(/\s+/g, ' ')
      .trim();
  };

  const formatNumber = (value) => {
    const number = Number(value || 0);

    if (!Number.isFinite(number)) {
      return '0';
    }

    return number.toLocaleString('en-US', {
      maximumFractionDigits: 2
    });
  };

  const formatDate = (value) => {
    if (!value) {
      return '-';
    }

    try {
      return new Date(value).toLocaleString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return '-';
    }
  };

  const formatDateTime = (value) => {
    return formatDate(value);
  };

  const getTypeIcon = (type) => {
    const icons = {
      recharge: '🔄',
      collection: '💵',
      transfer: '↔️',
      expense: '💸',
      company_settlement: '🏢'
    };

    return icons[type] || '💳';
  };

  const getTypeLabel = (type) => {
    const labels = {
      recharge: 'شحن',
      collection: 'قبض',
      transfer: 'تحويل',
      expense: 'مصروف',
      company_settlement: 'تسديد شركة'
    };

    return labels[type] || type || '-';
  };

  const getStoreName = (storeId) => {
    return stores.find(store => store.id === storeId)?.name || '-';
  };

  const getWalletName = (walletId) => {
    return wallets.find(wallet => wallet.id === walletId)?.name || '-';
  };

  const getUserName = (userId) => {
    return users.find(item => item.id === userId)?.name || '-';
  };


  const getFollowupStyle = (priority) => {
    const styles = {
      normal: 'bg-gray-50 border-gray-200 text-gray-800',
      watch: 'bg-yellow-50 border-yellow-200 text-yellow-800',
      warning: 'bg-orange-50 border-orange-200 text-orange-800',
      late: 'bg-red-50 border-red-200 text-red-800',
      very_late: 'bg-red-100 border-red-400 text-red-900'
    };

    return styles[priority] || styles.normal;
  };

  const getBalanceTextColor = (value) => {
    const number = Number(value || 0);
    if (number > 0) return 'text-green-200';
    if (number < 0) return 'text-red-200';
    return 'text-gray-200';
  };

  const getStoreFollowupReason = (store) => {
    const days = Number(store.followup_age_days || 0);

    if (!store.last_collection_at && !store.last_transaction_at) {
      return 'لا توجد عملية قبض مسجلة';
    }

    if (days === 0) return 'آخر متابعة اليوم';
    if (days === 1) return 'آخر قبض منذ يوم واحد';

    return `آخر قبض منذ ${days} أيام`;
  };

  const menuItems = [
    {
      id: 1,
      icon: '📊',
      title: 'لوحة التحكم',
      description: 'عرض الإحصائيات والمؤشرات المالية',
      path: '/dashboard',
      color: 'from-blue-500 to-blue-700'
    },
    {
      id: 2,
      icon: '🏪',
      title: 'المحلات',
      description: 'إدارة المحلات والأرصدة والديون',
      path: '/stores',
      color: 'from-green-500 to-green-700'
    },
    {
      id: 3,
      icon: '💰',
      title: 'المحافظ',
      description: 'إدارة المحافظ الرسمية الأربع',
      path: '/wallets',
      color: 'from-yellow-500 to-orange-600'
    },
    {
      id: 4,
      icon: '👨‍💼',
      title: 'الموظفين',
      description: 'إدارة المستخدمين والصلاحيات',
      path: '/employees',
      color: 'from-purple-500 to-purple-700'
    },
    {
      id: 5,
      icon: '💳',
      title: 'العمليات المالية',
      description: 'شحن، قبض، تحويل، مصروف، وتسديد',
      path: '/transactions',
      color: 'from-red-500 to-red-700'
    },
    {
      id: 6,
      icon: '💼',
      title: 'رصيدي',
      description: 'رصيد المستخدم وعملياته وتأكيد التحويلات',
      path: '/user-balance',
      color: 'from-emerald-500 to-emerald-700'
    },
    {
      id: 10,
      icon: '📈',
      title: 'التقارير',
      description: 'تقارير PDF و Excel',
      path: '/reports',
      color: 'from-indigo-500 to-indigo-700'
    },
    {
      id: 7,
      icon: '📋',
      title: 'سجل التدقيق',
      description: 'تتبع العمليات والتغييرات',
      path: '/audit-log',
      color: 'from-pink-500 to-pink-700'
    },
    {
      id: 8,
      icon: '🔔',
      title: 'إشعارات النظام',
      description: 'آخر التنبيهات والأحداث المهمة',
      path: '/notifications',
      color: 'from-orange-500 to-orange-700'
    },
    {
      id: 9,
      icon: '⚙️',
      title: 'الإعدادات',
      description: 'بيانات الشركة والنسخ الاحتياطي',
      path: '/settings',
      color: 'from-gray-600 to-gray-800'
    }
  ];

  const recentTransactions = useMemo(() => {
    return [...transactions]
      .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0))
      .slice(0, 10);
  }, [transactions]);

  const storesNeedCollection = useMemo(() => {
    if (homeFinanceSummary?.stores_need_followup?.length) {
      return homeFinanceSummary.stores_need_followup.slice(0, 8);
    }

    const now = Date.now();
    const oneDay = 24 * 60 * 60 * 1000;

    const lastCollectionByStore = new Map();

    transactions
      .filter(tx => tx.type === 'collection' && tx.store_id)
      .forEach(tx => {
        const currentDate = new Date(tx.created_at || 0).getTime();
        const previousDate = lastCollectionByStore.get(tx.store_id) || 0;

        if (currentDate > previousDate) {
          lastCollectionByStore.set(tx.store_id, currentDate);
        }
      });

    return stores
      .filter(store => (store.status || 'active') === 'active' && Number(store.balance || store.total_debt || 0) > 0)
      .map(store => {
        const lastCollectionAt = lastCollectionByStore.get(store.id) || null;
        const hoursSinceCollection = lastCollectionAt
          ? Math.floor((now - lastCollectionAt) / (60 * 60 * 1000))
          : null;

        const ageDays = hoursSinceCollection === null ? 999 : Math.floor(hoursSinceCollection / 24);

        let priority = 'normal';
        let priority_label = 'متابعة عادية';

        if (ageDays >= 14) {
          priority = 'very_late';
          priority_label = 'متأخر جداً';
        } else if (ageDays >= 7) {
          priority = 'late';
          priority_label = 'متأخر';
        } else if (ageDays >= 4) {
          priority = 'warning';
          priority_label = 'يحتاج متابعة';
        } else if (ageDays >= 2) {
          priority = 'watch';
          priority_label = 'قيد المتابعة';
        }

        return {
          ...store,
          lastCollectionAt,
          hoursSinceCollection,
          followup_age_days: ageDays === 999 ? 0 : ageDays,
          followup_priority: priority,
          followup_priority_label: priority_label
        };
      })
      .filter(store => {
        if (!store.lastCollectionAt) {
          return true;
        }

        return now - store.lastCollectionAt >= oneDay;
      })
      .sort((a, b) => {
        const aDays = Number(a.followup_age_days || 0);
        const bDays = Number(b.followup_age_days || 0);

        if (bDays !== aDays) return bDays - aDays;
        return Number(b.balance || b.total_debt || 0) - Number(a.balance || a.total_debt || 0);
      })
      .slice(0, 8);
  }, [stores, transactions, homeFinanceSummary]);

  const searchResults = useMemo(() => {
    const query = normalizeArabic(searchText);

    if (!query) {
      return {
        stores: [],
        transactions: [],
        users: [],
        wallets: [],
        notifications: [],
        notes: []
      };
    }

    const scoreMatch = (text) => {
      const normalizedText = normalizeArabic(text);

      if (!normalizedText) return 0;
      if (normalizedText === query) return 100;
      if (normalizedText.startsWith(query)) return 80;
      if (normalizedText.includes(query)) return 50;

      return 0;
    };

    const buildSearchText = (fields) => fields.filter(Boolean).join(' | ');

    const filterAndScore = (items, makeText) => {
      return items
        .map(item => ({
          item,
          score: scoreMatch(makeText(item))
        }))
        .filter(result => result.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 8)
        .map(result => result.item);
    };

    return {
      stores: filterAndScore(stores, store => buildSearchText([
        store.id,
        store.name,
        store.phone,
        store.owner_phone,
        store.owner_name,
        store.address,
        store.map_address,
        store.status,
        store.balance,
        store.total_debt
      ])),
      transactions: filterAndScore(transactions, tx => buildSearchText([
        tx.id,
        tx.type,
        getTypeLabel(tx.type),
        tx.amount,
        tx.description,
        tx.status,
        tx.store_id,
        getStoreName(tx.store_id),
        tx.wallet_id,
        getWalletName(tx.wallet_id),
        tx.user_id,
        getUserName(tx.user_id),
        formatDate(tx.created_at)
      ])),
      users: filterAndScore(users, item => buildSearchText([
        item.id,
        item.name,
        item.role,
        item.created_at
      ])),
      wallets: filterAndScore(wallets, wallet => buildSearchText([
        wallet.id,
        wallet.name,
        wallet.type,
        wallet.balance,
        wallet.status
      ])),
      notifications: filterAndScore(notifications, notification => buildSearchText([
        notification.id,
        notification.title,
        notification.message,
        notification.type,
        notification.created_at
      ])),
      notes: filterAndScore(generalNotes, note => buildSearchText([
        note.id,
        note.note,
        note.priority,
        note.user_name,
        note.created_at
      ]))
    };
  }, [searchText, stores, transactions, users, wallets, notifications, generalNotes]);


  const pendingTransferItems = useMemo(() => {
    const pendingStatuses = new Set(['pending', 'waiting', 'awaiting', 'requested', 'created', 'new']);
    const closedStatuses = new Set(['confirmed', 'completed', 'cancelled', 'canceled', 'rejected', 'failed']);

    return transactions.filter(tx => {
      const type = String(tx.type || '').toLowerCase();
      const status = String(tx.status || '').toLowerCase();

      const isTransfer = ['transfer', 'transfer_request', 'transfer_out', 'transfer_in'].includes(type) || type.includes('transfer');
      if (!isTransfer) return false;
      if (closedStatuses.has(status)) return false;
      if (!status) return false;

      return pendingStatuses.has(status) || status.includes('pending') || status.includes('await');
    });
  }, [transactions]);

  const pendingMasrafiItems = useMemo(() => {
    const closedStatuses = new Set(['confirmed', 'completed', 'cancelled', 'canceled', 'rejected', 'failed']);

    return transactions.filter(tx => {
      const type = String(tx.type || '').toLowerCase();
      const status = String(tx.status || '').toLowerCase();
      const description = normalizeArabic(tx.description || '');

      const isMasrafi = type.includes('masraf') || type.includes('bank') || description.includes('مصرفي');
      if (!isMasrafi) return false;
      if (closedStatuses.has(status)) return false;

      return status.includes('pending') || status.includes('await') || status === 'waiting' || status === 'new' || status === 'created';
    });
  }, [transactions]);

  const hasSearchResults = Object.values(searchResults).some(group => group.length > 0);

  const summary = dashboard?.summary || {};
  const dailyStats = dashboard?.daily_stats || {};
  const pendingTransfersCount = Number(homeAlerts?.pending_transfers_count ?? homeFinanceSummary?.pending_transfers_count ?? 0) || pendingTransferItems.length;
  const pendingTransfersAmount = Number(homeAlerts?.pending_transfers_amount || 0) || pendingTransferItems.reduce((total, tx) => total + Number(tx.amount || 0), 0);
  const pendingMasrafiCount = Number(homeAlerts?.pending_masrafi_count ?? dashboard?.summary?.pending_masrafi_count ?? 0) || pendingMasrafiItems.length;
  const pendingMasrafiAmount = Number(homeAlerts?.pending_masrafi_amount || dashboard?.summary?.pending_masrafi_amount || 0) || pendingMasrafiItems.reduce((total, tx) => total + Number(tx.amount || 0), 0);
  const overdueStoresCount = storesNeedCollection.length;
  const veryLateStoresCount = storesNeedCollection.filter(store => store.followup_priority === 'very_late').length;

  return (
    <div className="min-h-screen bg-gray-50 cf6h-home-shell" dir="rtl">
      {/* Header */}
      <div className="bg-gradient-to-br from-orange-500 via-orange-400 to-white text-white p-4 md:p-8 shadow-lg cf6h-hero">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <div className="inline-flex items-center gap-3 bg-white/10 rounded-full px-4 py-2 text-sm mb-4 brand-chip">
                <img src="/system-logo.jpeg" alt="شعار النظام" className="w-8 h-8 rounded-lg object-contain bg-white p-1 brand-logo-mini" />
                <span>خاص بفريق العمل</span>
              </div>

              <h1 className="text-3xl md:text-5xl font-bold flex items-center gap-3 flex-wrap">
                <img src="/system-logo.jpeg" alt="شعار النظام" className="w-14 h-14 rounded-2xl object-contain bg-white p-1 shadow brand-logo-interactive" />
                <span>نظام الإشارة الإداري</span>
              </h1>

              <p className="text-orange-100 mt-3 text-sm md:text-base">
                إدارة المحلات، المحافظ، العمليات المالية، والتنبيهات اليومية
              </p>
            </div>

            <div className="bg-white/10 rounded-2xl p-4 min-w-full lg:min-w-[230px] border border-white/10">
              <p className="text-orange-100 text-sm">مرحباً</p>
              <p className="font-bold text-white text-xl mt-1">
                {user?.name || 'مستخدم'}
              </p>
              <p className="text-orange-200 text-xs mt-1">
                الصلاحية: {user?.role || '-'}
              </p>

              <button
                onClick={handleLogout}
                className="mt-4 bg-red-500 hover:bg-red-600 text-white text-sm font-bold py-3 px-4 rounded-xl transition w-full"
              >
                🚪 خروج
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {dataError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 mb-6">
            {dataError}
          </div>
        )}

        {homeBroadcast && (
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-300 text-orange-950 rounded-2xl p-4 md:p-5 mb-6 shadow-sm">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <div className="inline-flex items-center gap-2 bg-orange-600 text-white rounded-full px-3 py-1 text-xs font-black mb-3">
                  📣 إشعار عام جديد
                </div>
                <h2 className="text-xl md:text-2xl font-black">{homeBroadcast.title || 'إشعار عام'}</h2>
                <p className="mt-2 leading-8 text-sm md:text-base whitespace-pre-wrap">{homeBroadcast.message}</p>
                <p className="text-xs text-orange-700 mt-3">سيبقى هذا الإشعار ظاهراً حتى يتم تعليمه كمقروء.</p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col gap-2 min-w-[180px]">
                <button
                  type="button"
                  onClick={markHomeBroadcastRead}
                  disabled={broadcastReadLoading}
                  className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold px-5 py-3 rounded-xl"
                >
                  {broadcastReadLoading ? 'جاري الحفظ...' : 'تمت القراءة'}
                </button>
                <button
                  type="button"
                  onClick={() => openPage('/notifications')}
                  className="bg-white hover:bg-orange-100 text-orange-800 border border-orange-200 font-bold px-5 py-3 rounded-xl"
                >
                  عرض الإشعارات
                </button>
              </div>
            </div>
          </div>
        )}

        {(pendingTransfersCount > 0 || pendingMasrafiCount > 0) && (
          <div className="pending-alert-card">
            <div className="pending-alert-glow" />
            <div className="pending-alert-main">
              <div className="pending-alert-icon">⚠️</div>
              <div className="pending-alert-text">
                <p className="pending-alert-title">يوجد لديك طلبات تحتاج مراجعة</p>
                <p className="pending-alert-desc">
                  {pendingTransfersCount > 0 && (
                    <span>طلبات تحويل معلقة: <b>{pendingTransfersCount}</b>{pendingTransfersAmount ? ` بقيمة ${formatNumber(pendingTransfersAmount)} LYD` : ''}</span>
                  )}
                  {pendingTransfersCount > 0 && pendingMasrafiCount > 0 && <span> · </span>}
                  {pendingMasrafiCount > 0 && (
                    <span>طلبات مصرفي معلقة: <b>{pendingMasrafiCount}</b>{pendingMasrafiAmount ? ` بقيمة ${formatNumber(pendingMasrafiAmount)} LYD` : ''}</span>
                  )}
                </p>
              </div>
            </div>

            <div className="pending-alert-actions">
              {pendingTransfersCount > 0 && (
                <button type="button" onClick={() => navigate('/user-balance')}>
                  قبول/رفض التحويل
                </button>
              )}
              {pendingMasrafiCount > 0 && (
                <button type="button" onClick={() => navigate('/masrafi-requests')} className="pending-alert-secondary">
                  طلبات مصرفي
                </button>
              )}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 mb-6 -mt-2 md:-mt-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                ⚡ إجراءات سريعة
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                الوصول السريع لأهم العمليات اليومية
              </p>
            </div>

            <button
              onClick={fetchHomeData}
              disabled={loading}
              className="bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold py-2 px-4 rounded-xl transition disabled:opacity-50"
            >
              {loading ? 'جاري التحديث...' : '🔄 تحديث البيانات'}
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <button onClick={() => navigate('/transactions')} className="bg-green-600 hover:bg-green-700 text-white font-bold py-4 px-3 rounded-xl transition">
              🔄 شحن
            </button>
            <button onClick={() => navigate('/transactions')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-4 px-3 rounded-xl transition">
              💵 قبض
            </button>
            <button onClick={() => navigate('/transactions')} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-4 px-3 rounded-xl transition">
              ↔️ تحويل
            </button>
            <button onClick={() => navigate('/transactions')} className="bg-red-600 hover:bg-red-700 text-white font-bold py-4 px-3 rounded-xl transition">
              💸 مصروف
            </button>
            <button onClick={() => openPage('/notifications')} className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-4 px-3 rounded-xl transition col-span-2 md:col-span-1">
              🔔 الإشعارات
            </button>
          </div>
        </div>

        {/* General Notes */}
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                📝 ملاحظات عامة للفريق
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                أي مستخدم يستطيع كتابة ملاحظة، وتظهر للجميع. الحذف مسموح لصاحب الملاحظة أو المالك فقط.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className="bg-orange-50 text-orange-700 border border-orange-100 rounded-full px-4 py-2 text-sm font-bold">
                {generalNotes.length} ملاحظة
              </span>
              <span className="bg-red-50 text-red-700 border border-red-100 rounded-full px-4 py-2 text-sm font-bold">
                {generalNotes.filter(note => note.priority === 'important').length} مهمة
              </span>
            </div>
          </div>

          {noteError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 mb-4">
              ⚠️ {noteError}
            </div>
          )}

          {noteSuccess && (
            <div className="bg-green-50 border border-green-200 text-green-700 rounded-2xl px-4 py-3 mb-4">
              ✅ {noteSuccess}
            </div>
          )}

          <form onSubmit={handleCreateNote} className="grid grid-cols-1 lg:grid-cols-5 gap-3 mb-5">
            <div className="lg:col-span-3">
              <textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder="اكتب ملاحظة تظهر لكل الفريق..."
                rows="3"
                className="w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50 resize-none"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1 px-1">
                <span>تظهر الملاحظة لكل الفريق</span>
                <span>{noteText.length}/1000</span>
              </div>
            </div>

            <select
              value={notePriority}
              onChange={(e) => setNotePriority(e.target.value)}
              className="border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 bg-gray-50"
            >
              <option value="normal">ملاحظة عادية</option>
              <option value="important">ملاحظة مهمة</option>
            </select>

            <button
              type="submit"
              disabled={noteSaving}
              className="bg-orange-600 hover:bg-orange-700 disabled:bg-orange-300 text-white font-bold py-4 px-5 rounded-2xl"
            >
              {noteSaving ? 'جاري النشر...' : 'نشر الملاحظة'}
            </button>
          </form>

          {generalNotes.length === 0 ? (
            <div className="text-center text-gray-500 bg-gray-50 rounded-2xl p-6">
              لا توجد ملاحظات عامة حالياً
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {generalNotes.slice(0, 9).map((note) => (
                  <GeneralNoteCard
                    key={note.id}
                    note={note}
                    canDelete={canDeleteNote(note)}
                    deleting={noteDeletingId === note.id}
                    onDelete={() => handleDeleteNote(note)}
                    formatDate={formatDate}
                  />
                ))}
              </div>

              {generalNotes.length > 9 && (
                <div className="mt-4 text-center text-gray-500 bg-gray-50 rounded-2xl p-4">
                  يتم عرض آخر 9 ملاحظات فقط. استخدم البحث العام للعثور على باقي الملاحظات.
                </div>
              )}
            </>
          )}
        </div>

        {/* Smart Global Search */}
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6 mb-6">
          <div className="mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              🔎 البحث العام الذكي
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              ابحث بأي شيء: رقم عملية، رقم هاتف، اسم محل، مستخدم، محفظة، مبلغ، وصف، أو نوع عملية
            </p>
          </div>

          <input
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="اكتب للبحث في كل النظام..."
            className="w-full border-2 border-gray-200 focus:border-orange-500 outline-none rounded-2xl p-4 text-lg bg-gray-50"
          />

          {searchText.trim() && (
            <div className="mt-5">
              {!hasSearchResults ? (
                <div className="text-center text-gray-500 bg-gray-50 rounded-xl p-6">
                  لا توجد نتائج مطابقة
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {searchResults.stores.length > 0 && (
                    <ResultBox title="🏪 المحلات" onOpen={() => navigate('/stores')}>
                      {searchResults.stores.map(store => (
                        <ResultItem
                          key={store.id}
                          title={store.name}
                          subtitle={`هاتف: ${store.phone || store.owner_phone || '-'} | الحالة: ${store.status || 'active'}`}
                          badge={`رصيد: ${formatNumber(store.balance || store.total_debt || 0)}`}
                          onClick={() => navigate('/stores')}
                        />
                      ))}
                    </ResultBox>
                  )}

                  {searchResults.transactions.length > 0 && (
                    <ResultBox title="💳 العمليات" onOpen={() => navigate('/transactions')}>
                      {searchResults.transactions.map(tx => (
                        <ResultItem
                          key={tx.id}
                          title={`${getTypeIcon(tx.type)} ${getTypeLabel(tx.type)} - ${formatNumber(tx.amount)}`}
                          subtitle={`${tx.description || '-'} | ${formatDate(tx.created_at)}`}
                          badge={`#${String(tx.id).slice(0, 8)}`}
                          onClick={() => navigate('/transactions')}
                        />
                      ))}
                    </ResultBox>
                  )}

                  {searchResults.users.length > 0 && (
                    <ResultBox title="👨‍💼 المستخدمون" onOpen={() => navigate('/employees')}>
                      {searchResults.users.map(item => (
                        <ResultItem
                          key={item.id}
                          title={item.name}
                          subtitle={`الصلاحية: ${item.role}`}
                          badge={String(item.id).slice(0, 8)}
                          onClick={() => navigate('/employees')}
                        />
                      ))}
                    </ResultBox>
                  )}

                  {searchResults.wallets.length > 0 && (
                    <ResultBox title="💰 المحافظ" onOpen={() => navigate('/wallets')}>
                      {searchResults.wallets.map(wallet => (
                        <ResultItem
                          key={wallet.id}
                          title={wallet.name}
                          subtitle={`النوع: ${wallet.type || '-'} | الحالة: ${wallet.status || 'active'}`}
                          badge={formatNumber(wallet.balance)}
                          onClick={() => navigate('/wallets')}
                        />
                      ))}
                    </ResultBox>
                  )}

                  {searchResults.notifications.length > 0 && (
                    <ResultBox title="🔔 الإشعارات" onOpen={() => openPage('/notifications')}>
                      {searchResults.notifications.map(notification => (
                        <ResultItem
                          key={notification.id}
                          title={notification.title}
                          subtitle={`${notification.message || '-'} | ${formatDate(notification.created_at)}`}
                          badge={notification.type || 'info'}
                          onClick={() => openPage('/notifications')}
                        />
                      ))}
                    </ResultBox>
                  )}

                  {searchResults.notes.length > 0 && (
                    <ResultBox title="📝 الملاحظات العامة" onOpen={() => {}}>
                      {searchResults.notes.map(note => (
                        <ResultItem
                          key={note.id}
                          title={note.note}
                          subtitle={`بواسطة: ${note.user_name || '-'} | ${formatDate(note.created_at)}`}
                          badge={note.priority === 'important' ? 'مهم' : 'عادي'}
                          onClick={() => {}}
                        />
                      ))}
                    </ResultBox>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Clickable Daily Overview Cards */}
        <div className="cf6h-overview-grid mb-6">
          <SummaryCard
            title="رصيدي"
            value={`${formatNumber(homeFinanceSummary?.balance || 0)} LYD`}
            note="اضغط لفتح صفحة رصيدي والتحويلات"
            color="orange"
            icon="💼"
            onClick={() => navigate('/user-balance')}
          />
          <SummaryCard
            title="إجمالي المصروفات"
            value={`${formatNumber(dailyStats.expenses)} LYD`}
            note="اضغط لعرض عمليات المصروف"
            color="red"
            icon="💸"
            onClick={() => navigate('/transactions?type=expense')}
          />
          <SummaryCard
            title="الطلبات المعلقة"
            value={pendingTransfersCount + pendingMasrafiCount}
            note={pendingTransfersCount || pendingMasrafiCount ? 'اضغط لمراجعة الطلبات' : 'لا توجد طلبات معلقة'}
            color="blue"
            icon="⏳"
            onClick={() => pendingTransfersCount > 0 ? navigate('/user-balance') : navigate('/masrafi-requests')}
          />
          <SummaryCard
            title="المحلات المتأخرة"
            value={overdueStoresCount}
            note={veryLateStoresCount > 0 ? `${veryLateStoresCount} متأخر جداً` : 'محلات تحتاج قبض / متابعة'}
            color="red"
            icon="⏰"
            onClick={() => navigate('/stores')}
          />
          <SummaryCard
            title="العمليات"
            value={transactions.filter(tx => tx.status !== 'cancelled').length}
            note="اضغط لفتح صفحة العمليات"
            color="amber"
            icon="🧾"
            onClick={() => navigate('/transactions')}
          />
          <SummaryCard
            title="المحلات"
            value={summary.active_stores ?? stores.filter(store => (store.status || 'active') === 'active').length}
            note="اضغط لفتح صفحة المحلات"
            color="green"
            icon="🏪"
            onClick={() => navigate('/stores')}
          />
        </div>

        {/* Alerts + Recent Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                ⏰ محلات تحتاج قبض / متابعة
              </h2>
              <button onClick={() => navigate('/stores')} className="text-orange-600 font-bold text-sm">
                عرض المحلات
              </button>
            </div>

            {storesNeedCollection.length === 0 ? (
              <div className="text-center text-gray-500 bg-gray-50 rounded-xl p-6">
                لا توجد محلات تحتاج متابعة حالياً
              </div>
            ) : (
              <div className="space-y-3">
                {storesNeedCollection.map(store => (
                  <div key={store.id} className={`border rounded-xl p-4 ${getFollowupStyle(store.followup_priority)}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="font-bold text-gray-900 truncate">{store.name}</p>

                        <p className="text-sm text-gray-700 mt-1">
                          {getStoreFollowupReason(store)}
                        </p>

                        <p className="text-sm font-bold mt-1">
                          الدين / الرصيد: {formatNumber(store.balance || store.total_debt || 0)} LYD
                        </p>

                        {(store.phone || store.owner_phone) && (
                          <p className="text-sm text-gray-500 mt-1">
                            الهاتف: {store.phone || store.owner_phone}
                          </p>
                        )}

                        <div className="flex flex-wrap gap-2 mt-3">
                          <button
                            onClick={() => navigate('/transactions')}
                            className="bg-green-600 hover:bg-green-700 text-white px-3 py-2 rounded-xl text-xs font-bold"
                          >
                            قبض سريع
                          </button>

                          <button
                            onClick={() => navigate('/stores')}
                            className="bg-gray-900 hover:bg-black text-white px-3 py-2 rounded-xl text-xs font-bold"
                          >
                            كشف الحساب
                          </button>
                        </div>
                      </div>

                      <span className="bg-white/70 text-gray-900 px-3 py-1 rounded-full text-xs font-bold shrink-0">
                        {store.followup_priority_label || 'متابعة'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
            <div className="flex items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                🧾 آخر 10 عمليات
              </h2>
              <button onClick={() => navigate('/transactions')} className="text-orange-600 font-bold text-sm">
                عرض العمليات
              </button>
            </div>

            {recentTransactions.length === 0 ? (
              <div className="text-center text-gray-500 bg-gray-50 rounded-xl p-6">
                لا توجد عمليات حالياً
              </div>
            ) : (
              <div className="space-y-3">
                {recentTransactions.map(tx => (
                  <div key={tx.id} className="border border-gray-100 bg-gray-50 rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-bold text-gray-900">
                          {getTypeIcon(tx.type)} {getTypeLabel(tx.type)}
                          <span className="text-green-700 mr-2">
                            {formatNumber(tx.amount)}
                          </span>
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          {tx.description || '-'}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(tx.created_at)}
                        </p>
                      </div>

                      <span className="bg-gray-200 text-gray-700 px-3 py-1 rounded-full text-xs">
                        #{String(tx.id).slice(0, 8)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Menu Grid */}
        <div className="bg-white rounded-2xl shadow-lg p-5 md:p-6">
          <div className="mb-5">
            <h2 className="text-2xl font-bold text-gray-900">
              أقسام النظام
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              كل صفحة لها تصميمها ووظيفتها الخاصة
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => openPage(item.path)}
                className="group text-right"
              >
                <div className={`bg-gradient-to-br ${item.color} rounded-2xl p-6 text-white shadow hover:shadow-xl transform hover:-translate-y-1 transition duration-300 h-full`}>
                  <div className="text-4xl mb-3">{item.icon}</div>
                  <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                  <p className="text-sm opacity-90">{item.description}</p>

                  {item.path === '/user-balance' && (
                    <div className="mt-4 bg-white/15 border border-white/10 rounded-2xl p-3">
                      <p className="text-xs opacity-90">الرصيد الحالي</p>
                      <p className={`text-2xl font-bold mt-1 ${getBalanceTextColor(homeFinanceSummary?.balance)}`}>
                        {formatNumber(homeFinanceSummary?.balance || 0)} LYD
                      </p>
                      <p className="text-xs opacity-90 mt-1">
                        {Number(homeFinanceSummary?.pending_transfers_count || 0) > 0
                          ? `${homeFinanceSummary.pending_transfers_count} تحويل يحتاج تأكيد`
                          : 'لا توجد تحويلات معلقة'}
                      </p>
                      <p className="text-[11px] opacity-75 mt-1">
                        آخر تحديث: {formatDateTime(homeFinanceSummary?.balance_updated_at)}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 text-sm opacity-80 group-hover:opacity-100 transition">
                    اضغط للدخول ←
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-900 text-gray-300 text-center p-6 mt-10">
        <p>© 2026 نظام الإشارة الإداري - جميع الحقوق محفوظة</p>
      </div>
    </div>
  );
}

function GeneralNoteCard({ note, canDelete, deleting, onDelete, formatDate }) {
  const important = note.priority === 'important';

  return (
    <div className={`rounded-2xl p-4 border ${important ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-100'}`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <span className={`px-3 py-1 rounded-full text-xs font-bold ${important ? 'bg-orange-200 text-orange-800' : 'bg-gray-200 text-gray-700'}`}>
          {important ? 'مهم' : 'عادي'}
        </span>

        {note.is_pinned && (
          <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-xs font-bold">
            مثبت
          </span>
        )}
      </div>

      <p className="text-gray-900 font-bold leading-7 whitespace-pre-wrap">
        {note.note}
      </p>

      <div className="mt-4 pt-4 border-t border-white/70 text-sm text-gray-500 space-y-1">
        <p>👤 بواسطة: {note.user_name || '-'}</p>
        <p>🕒 {formatDate(note.created_at)}</p>
      </div>

      {canDelete && (
        <button
          onClick={onDelete}
          disabled={deleting}
          className="mt-4 w-full bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white font-bold py-3 rounded-xl"
        >
          {deleting ? 'جاري الحذف...' : 'حذف الملاحظة'}
        </button>
      )}
    </div>
  );
}

function SummaryCard({ title, value, note, color, icon, onClick }) {
  const colors = {
    orange: 'cf6h-summary-orange',
    amber: 'cf6h-summary-amber',
    green: 'cf6h-summary-green',
    red: 'cf6h-summary-red',
    blue: 'cf6h-summary-blue',
    purple: 'cf6h-summary-purple'
  };

  const content = (
    <>
      <div className="cf6h-summary-top">
        <span className="cf6h-summary-icon">{icon || '📌'}</span>
        <span className="cf6h-summary-arrow">↗</span>
      </div>
      <p className="cf6h-summary-title">{title}</p>
      <p className="cf6h-summary-value">{value}</p>
      <p className="cf6h-summary-note">{note}</p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`cf6h-summary-card ${colors[color] || colors.orange}`}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={`cf6h-summary-card ${colors[color] || colors.orange}`}>
      {content}
    </div>
  );
}

function ResultBox({ title, children, onOpen }) {
  return (
    <div className="border border-gray-100 bg-gray-50 rounded-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-gray-900">{title}</h3>
        <button onClick={onOpen} className="text-orange-600 text-sm font-bold">
          فتح
        </button>
      </div>

      <div className="space-y-2">
        {children}
      </div>
    </div>
  );
}

function ResultItem({ title, subtitle, badge, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-right bg-white hover:bg-orange-50 border border-gray-100 rounded-xl p-3 transition"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-bold text-gray-900 truncate">{title}</p>
          <p className="text-xs text-gray-500 mt-1 line-clamp-2">{subtitle}</p>
        </div>

        <span className="shrink-0 bg-gray-100 text-gray-700 px-2 py-1 rounded-lg text-xs">
          {badge}
        </span>
      </div>
    </button>
  );
}

export default Home;
